import * as THREE from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import type { MeasureEntityRef } from '../types/measure-types';
import {
    MeasureDistancePrimitiveKind,
    type MeasureClosestPointResult,
    type MeasureDistanceGeometry,
} from '../types/measure-distance-types';
import { solveFarthestPointsBetweenGeometries } from '../utils/measureFarthestPointGeometry';
import { MeasureEntityGeometryAdapter } from './MeasureEntityGeometryAdapter';
import { computeDistanceBetweenPointsMillimeter } from '../math/MeasureNumericKernel';

/** 判定退化距离与“不绘制测量线”的容差（毫米）。 */
const MEASURE_FARTHEST_DISTANCE_EPSILON_MM = 0.0001;

/**
 * 两测量实体最远点求解器。
 *
 * 职责：在圆弧/圆最大距离子模式下，计算两选中几何体之间的全局最远距离点对。
 */
export class MeasureFarthestPointResolver {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 几何适配器。 */
    private readonly geometryAdapter: MeasureEntityGeometryAdapter;

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
        this.geometryAdapter = new MeasureEntityGeometryAdapter(viewer);
    }

    /**
     * 求解两测量实体之间的最远点。
     *
     * @param firstEntity 先选实体（最远点 `from` 落在该实体上）。
     * @param secondEntity 后选实体（最远点 `to` 落在该实体上）。
     * @returns 最远点结果；无法求解时返回 null。
     */
    public resolve(
        firstEntity: MeasureEntityRef,
        secondEntity: MeasureEntityRef,
    ): MeasureClosestPointResult | null {
        const geometryA = this.geometryAdapter.adapt(firstEntity);
        const geometryB = this.geometryAdapter.adapt(secondEntity);
        if (!geometryA || !geometryB) {
            return null;
        }

        const pair = solveFarthestPointsBetweenGeometries(geometryA, geometryB);
        if (!pair) {
            return null;
        }

        const distance = computeDistanceBetweenPointsMillimeter(pair.pointA, pair.pointB);
        const shouldRenderMeasureLines = distance > MEASURE_FARTHEST_DISTANCE_EPSILON_MM;

        return {
            from: pair.pointA.clone(),
            to: pair.pointB.clone(),
            distance,
            shouldRenderMeasureLines,
        };
    }

    /**
     * 求世界坐标点与测量实体之间的最远点参考点对。
     *
     * @param worldPoint 世界坐标点。
     * @param entityRef 目标测量实体。
     * @param pointSideIsFirst 世界坐标点是否对应先选实体侧。
     * @returns 最远点结果；无法求解时返回 null。
     */
    public resolveBetweenWorldPointAndEntity(
        worldPoint: THREE.Vector3,
        entityRef: MeasureEntityRef,
        pointSideIsFirst: boolean,
    ): MeasureClosestPointResult | null {
        const pointGeometry: MeasureDistanceGeometry = {
            kind: MeasureDistancePrimitiveKind.Point,
            position: worldPoint.clone(),
        };
        const entityGeometry = this.geometryAdapter.adapt(entityRef);
        if (!entityGeometry) {
            return null;
        }

        const geometryA = pointSideIsFirst ? pointGeometry : entityGeometry;
        const geometryB = pointSideIsFirst ? entityGeometry : pointGeometry;
        const pair = solveFarthestPointsBetweenGeometries(geometryA, geometryB);
        if (!pair) {
            return null;
        }

        const distance = computeDistanceBetweenPointsMillimeter(pair.pointA, pair.pointB);
        return {
            from: pair.pointA.clone(),
            to: pair.pointB.clone(),
            distance,
            shouldRenderMeasureLines: distance > MEASURE_FARTHEST_DISTANCE_EPSILON_MM,
        };
    }
}
